// Centralized client-side helpers for dynamic Task Log statuses
// (configured in Settings → Control Panel → Task Log Status)

import {
  Clock, Loader2, PauseCircle, CheckCircle2, Ban,
  Circle, AlertCircle, Hourglass, Flag, Pin, Star, Zap, Send, Eye,
  Archive, Lock, Unlock,
} from 'lucide-react';

// Lucide icon name → component lookup (only icons whitelisted in the admin UI)
export const STATUS_ICONS = {
  Clock, Loader2, PauseCircle, CheckCircle2, Ban,
  Circle, AlertCircle, Hourglass, Flag, Pin, Star, Zap, Send, Eye,
  Archive, Lock, Unlock,
};

// Tailwind color name → tailwind class triples (badge/pill/dot/raw-color)
export const STATUS_COLOR_MAP = {
  slate:  { pill: 'bg-slate-100 text-slate-700 border-slate-200',     chart: '#64748B', dotBg: 'bg-slate-400' },
  yellow: { pill: 'bg-yellow-100 text-yellow-700 border-yellow-200', chart: '#F59E0B', dotBg: 'bg-yellow-500' },
  blue:   { pill: 'bg-blue-100 text-blue-700 border-blue-200',       chart: '#3B82F6', dotBg: 'bg-blue-500' },
  orange: { pill: 'bg-orange-100 text-orange-700 border-orange-200', chart: '#F97316', dotBg: 'bg-orange-500' },
  green:  { pill: 'bg-green-100 text-green-700 border-green-200',    chart: '#10B981', dotBg: 'bg-green-500' },
  red:    { pill: 'bg-red-100 text-red-700 border-red-200',          chart: '#EF4444', dotBg: 'bg-red-500' },
  violet: { pill: 'bg-violet-100 text-violet-700 border-violet-200', chart: '#8B5CF6', dotBg: 'bg-violet-500' },
  pink:   { pill: 'bg-pink-100 text-pink-700 border-pink-200',       chart: '#EC4899', dotBg: 'bg-pink-500' },
  amber:  { pill: 'bg-amber-100 text-amber-700 border-amber-200',    chart: '#D97706', dotBg: 'bg-amber-500' },
  emerald:{ pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', chart: '#059669', dotBg: 'bg-emerald-500' },
};

export const ICON_CHOICES = Object.keys(STATUS_ICONS);
export const COLOR_CHOICES = Object.keys(STATUS_COLOR_MAP);

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _cached = null;
let _inflight = null;

// Hard-coded fallback so the UI still renders if the API call errors / is slow
const FALLBACK_STATUSES = [
  { value: 'pending',     label: 'Pending',     color: 'yellow', icon: 'Clock',        order: 1, is_terminal: false, is_built_in: true },
  { value: 'in_progress', label: 'In Progress', color: 'blue',   icon: 'Loader2',      order: 2, is_terminal: false, is_built_in: true },
  { value: 'hold',        label: 'Hold',        color: 'orange', icon: 'PauseCircle',  order: 3, is_terminal: false, is_built_in: true },
  { value: 'completed',   label: 'Completed',   color: 'green',  icon: 'CheckCircle2', order: 4, is_terminal: true,  is_built_in: true },
  { value: 'defunct',     label: 'Defunct',     color: 'red',    icon: 'Ban',          order: 5, is_terminal: true,  is_built_in: true },
];

export const fetchTaskStatuses = async (force = false) => {
  if (_cached && !force) return _cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await fetch(`${API_URL}/api/masters/task-statuses`, { credentials: 'include' });
      if (!r.ok) throw new Error('fetch failed');
      const j = await r.json();
      const sorted = (Array.isArray(j) ? j : []).sort((a, b) => (a.order || 0) - (b.order || 0));
      _cached = sorted.length ? sorted : FALLBACK_STATUSES;
    } catch {
      _cached = FALLBACK_STATUSES;
    } finally {
      _inflight = null;
    }
    return _cached;
  })();
  return _inflight;
};

export const invalidateStatusCache = () => { _cached = null; };

export const getStatusMeta = (statuses, value) => {
  const s = (statuses || []).find(x => x.value === value);
  if (s) {
    const colors = STATUS_COLOR_MAP[s.color] || STATUS_COLOR_MAP.slate;
    return { ...s, ...colors, Icon: STATUS_ICONS[s.icon] || Circle };
  }
  // Unknown status — show neutral fallback so UI doesn't break
  return {
    value, label: value, color: 'slate', icon: 'Circle',
    ...STATUS_COLOR_MAP.slate, Icon: Circle, is_terminal: false, is_built_in: false,
  };
};
